/**
 * ч.1425: /feedback/<токен> — переписка відвідувача, який написав через «Зворотний звʼязок» на
 * головній. Облікового запису немає, тому лист містить лише це посилання (Норма #34): відповідь
 * людина читає тут і тут же може дописати.
 */
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { feedback, type FeedbackMessage } from '@/api/feedback';
import BrandLogo from '@/shared/components/BrandLogo';
import { extractApiError } from '@/utils/extractApiError';

const AUTHOR: Record<FeedbackMessage['author_kind'], string> = {
  user: 'Ви', agent: 'Підтримка Облік+', staff: 'Спеціаліст Облік+',
};

function when(iso: string): string {
  return new Date(iso).toLocaleString('uk-UA',
    { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function FeedbackThreadPage() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const thread = useQuery({
    queryKey: ['feedback-thread', token], queryFn: () => feedback.thread(token),
    refetchInterval: 15000, retry: false,
  });
  const send = useMutation({
    mutationFn: () => feedback.send(token, body.trim()),
    onSuccess: (data) => {
      qc.setQueryData(['feedback-thread', token], data);
      setBody('');
      setError(null);
    },
    onError: (e) => setError(extractApiError(e, 'Не вдалося надіслати повідомлення')),
  });
  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (body.trim()) send.mutate();
  }
  return (
    <div className="public-help">
      <header className="public-help__bar">
        <a href="/" className="public-help__logo" aria-label="Облік+ — на головну"><BrandLogo /></a>
      </header>
      <main className="feedback-thread">
        {thread.isLoading && <p>Завантаження…</p>}
        {thread.isError && <p role="alert">Звернення не знайдено. Перевірте посилання з листа.</p>}
        {thread.data && (
          <div className="card">
            <h2 style={{ margin: 0 }}>{thread.data.title}</h2>
            <div className="feedback-thread__id">Звернення {thread.data.public_id}</div>
            {thread.data.messages.map((m) => (
              <div key={m.created_at + m.author_kind}
                className={`feedback-thread__msg feedback-thread__msg--${m.author_kind}`}>
                <div className="feedback-thread__meta">{AUTHOR[m.author_kind]} · {when(m.created_at)}</div>
                <div className="feedback-thread__body">{m.body}</div>
              </div>
            ))}
            <form onSubmit={onSubmit}>
              <div className="field">
                <label htmlFor="feedback-reply">Ваше повідомлення</label>
                <textarea id="feedback-reply" rows={4} maxLength={4000} value={body}
                  onChange={(e) => setBody(e.target.value)} />
              </div>
              <button type="submit" className="primary" disabled={send.isPending || !body.trim()}>
                {send.isPending ? 'надсилаємо…' : 'надіслати'}
              </button>
              {error && <div className="error" role="alert">{error}</div>}
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
